import { useRef, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import { useSetDefaultCampaignMutation } from "@/pages/Private/redux/list/list-campaign.api";
import { useUpdateListMutation } from "@/pages/Private/redux/list/list.api";

import { UpdateType } from "@/enum/list.enum";

import { describeSaveFailure } from "@/utils/describeSaveFailure";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { Campaign } from "../../schema/campaign";
import {
	buildClonedCampaignConfig,
	ClonableCampaign,
} from "../../components/CampaignOverview/utils/buildClonedCampaignConfig.util";
import {
	buildStopDateConfig,
	computeStopDateOverwrites,
	type StopDateOverwrite,
} from "./ListEditCopywriting.helpers";

import type { CopywritingCampaign } from "./ListEditCopywriting";

interface UseCampaignActionsParams {
	listId: number;
	campaignConfig?: Record<string, Campaign> | null;
	/** The list event's end date, used by the "Apply event end date" bulk action. */
	eventEndDate?: string | null;
	/** The end date a duplicated or copied campaign starts with (`eventStopDatePrefill`). */
	copyStopDate?: string | null;
}

/**
 * The campaign mutations of the Outreach step: copy-from-another-list, clone, delete, set-as-default
 * and the bulk "apply the event end date as every campaign's stop date" action — together with the
 * confirmation-modal state each of them needs. Lifted out of `ListEditCopywriting` so the page is
 * only composition (PREC-3687 block 1).
 */
export const useCampaignActions = ({
	listId,
	campaignConfig,
	eventEndDate,
	copyStopDate,
}: UseCampaignActionsParams) => {
	const [updateList] = useUpdateListMutation();
	const [setDefaultCampaign] = useSetDefaultCampaignMutation();

	const [selectedCampaign, setSelectedCampaign] = useState<CopywritingCampaign | null>(null);
	const selectedCampaignRef = useRef<CopywritingCampaign | null>(null);

	selectedCampaignRef.current = selectedCampaign;
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showSetAsDefaultModal, setShowSetAsDefaultModal] = useState<boolean>(false);

	// "Apply event end date" overwrite confirmation (PREC-3540): the campaigns whose existing stop
	// date would be overwritten, surfaced in the modal before we persist.
	const [stopDateOverwrites, setStopDateOverwrites] = useState<StopDateOverwrite[]>([]);
	const [showApplyStopDateModal, setShowApplyStopDateModal] = useState<boolean>(false);

	// Only the new campaigns travel, as a merge (PREC-4026): replacing the whole map from this
	// page's cache is how a stale copy of a sibling campaign's steps got persisted.
	const duplicateCampaigns = async (campaigns: Campaign[]) => {
		let newCampaigns: Record<string, ClonableCampaign> = {};

		for (const campaign of campaigns) {
			newCampaigns = buildClonedCampaignConfig(newCampaigns, campaign, uuidv4(), copyStopDate);
		}

		await updateList({
			id: listId,
			updateType: UpdateType.CAMPAIGN_CONFIG_MERGE,
			campaignConfig: JSON.stringify(newCampaigns),
			campaignWriteOrigin: "copy-from-list",
		}).unwrap();
	};

	// The clone icon has no pending state of its own, so a double click used to save two copies
	// (web CLAUDE.md §24c): a click while a copy is still being saved is ignored.
	const isCloningRef = useRef(false);

	const cloneCampaign = async (campaign: CopywritingCampaign) => {
		if (isCloningRef.current) {
			return;
		}

		isCloningRef.current = true;
		const newCampaignUuid = uuidv4();

		try {
			await updateList({
				id: listId,
				updateType: UpdateType.CAMPAIGN_CONFIG,
				campaignConfig: JSON.stringify(
					buildClonedCampaignConfig(campaignConfig, campaign, newCampaignUuid, copyStopDate)
				),
				campaignIdToUpdate: newCampaignUuid,
				campaignWriteOrigin: "duplicate",
			}).unwrap();
		} finally {
			isCloningRef.current = false;
		}
	};

	const openDeleteModal = (campaign: CopywritingCampaign) => {
		setSelectedCampaign(campaign);
		setShowDeleteModal(true);
	};

	const openSetAsDefaultModal = (campaign: CopywritingCampaign) => {
		setSelectedCampaign(campaign);
		setShowSetAsDefaultModal(true);
	};

	const closeDeleteModal = () => setShowDeleteModal(false);

	const closeSetAsDefaultModal = () => setShowSetAsDefaultModal(false);

	// The delete itself runs in DeleteCampaignModal (PREC-4258), which confirms with the counted
	// leads first; this only closes the dialog once the campaign is gone.
	// An answer that belongs to another campaign than the one selected changes nothing.
	const onCampaignDeleted = ({ campaignUuid }: { campaignUuid: string }) => {
		if (selectedCampaignRef.current?.id !== campaignUuid) {
			return;
		}

		setShowDeleteModal(false);
		setSelectedCampaign(null);
	};

	// The API moves only the flag (PREC-4026); re-uploading the campaign object from this page's
	// cache used to persist whatever stale steps the cache held.
	const setAsDefaultCampaign = async (campaign: CopywritingCampaign) => {
		await setDefaultCampaign({ id: listId, campaignUuid: campaign.id as string }).unwrap();

		setShowSetAsDefaultModal(false);
		setSelectedCampaign(null);
	};

	// Persist the event end date as the campaign stop date. `overwriteExisting` overwrites campaigns
	// that already have a stop date — only ever true after the user confirms in the modal (PREC-3540).
	const persistEventStopDate = async (overwriteExisting: boolean) => {
		if (!campaignConfig || !eventEndDate) {
			return;
		}

		// A merge, never a replace (PREC-4258): a replace drops every campaign this page's cache does
		// not hold, such as one created in another tab, and a dropped campaign is now deleted with
		// its leads released and its pending steps cancelled.
		try {
			await updateList({
				id: listId,
				updateType: UpdateType.CAMPAIGN_CONFIG_MERGE,
				campaignWriteOrigin: "event-stop-date",
				campaignConfig: JSON.stringify(
					buildStopDateConfig(campaignConfig, eventEndDate, overwriteExisting)
				),
			}).unwrap();

			showGlobalToast("Event end date applied to campaign stop dates.", 4000, "success");
		} catch (error) {
			// A rejected mutation reaches the user through nothing else (rtkRejectQueryMiddleware acts
			// on queries only), so the API's reason is said here, a structure lock included (PREC-4616).
			showGlobalToast(describeSaveFailure(error, "The event end date could not be applied."));
		}
	};

	// Bulk-apply the event end date as every campaign's stop date (PREC-3520 / PREC-3540). Campaigns
	// without a stop date are filled silently; if any already has a different stop date, the overwrite
	// is confirmed in a modal first.
	const applyEventStopDate = () => {
		if (!campaignConfig || !eventEndDate) {
			return;
		}

		const overwrites = computeStopDateOverwrites(campaignConfig, eventEndDate);

		if (overwrites.length > 0) {
			setStopDateOverwrites(overwrites);
			setShowApplyStopDateModal(true);

			return;
		}

		persistEventStopDate(false);
	};

	const closeApplyStopDateModal = () => {
		setShowApplyStopDateModal(false);
		setStopDateOverwrites([]);
	};

	const confirmApplyStopDate = async () => {
		await persistEventStopDate(true);

		closeApplyStopDateModal();
	};

	return {
		selectedCampaign,
		showDeleteModal,
		showSetAsDefaultModal,
		showApplyStopDateModal,
		stopDateOverwrites,
		duplicateCampaigns,
		cloneCampaign,
		openDeleteModal,
		openSetAsDefaultModal,
		closeDeleteModal,
		closeSetAsDefaultModal,
		onCampaignDeleted,
		setAsDefaultCampaign,
		applyEventStopDate,
		closeApplyStopDateModal,
		confirmApplyStopDate,
	};
};
